import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, ExternalLink,
  Package, ShoppingCart, Factory, ClipboardCheck, Truck, CheckCircle2,
  Clock, Loader, Inbox, Building2, PackageOpen, PackageX, PhoneCall, Warehouse,
  DollarSign, XCircle, RotateCcw, ShieldAlert, ShieldOff,
  PlusCircle, MessageSquare, FileEdit, ChevronDown, ChevronRight,
  PackagePlus, Recycle, ArrowDownToLine, Undo2, Wrench, SlidersHorizontal, Trash2, Pencil, Layers,
} from "lucide-react";
import axios from "axios";
import { STAGE_COLORS, STAGE_PATHS } from "./utils";
import { LiveWake } from "../LiveIndicator";
import { carrierTrackingUrl, openTrackingUrl } from "../StagePage/utils";
import { ClientsContext, ClientGroupsContext } from "@/App";

const API = process.env.REACT_APP_BACKEND_URL;

// Canonical app stage → icon (mirrors /components/Layout.js nav).
const STAGE_ICONS = {
  "PO Received":     Package,
  "Procurement":     ShoppingCart,
  "Manufacturing":   Factory,
  "Quality Control": ClipboardCheck,
  "Dispatch":        Truck,
  "Completed":       CheckCircle2,
  "Stores":          Warehouse,
};

// Lower-cased value → { icon, tone } for the new-value pill.
// TONES MIRROR THE STAGE TABLES (StageRowDesktop's STATUS_TONE_BY_VALUE):
// done = emerald, in-flight = blue, waiting = amber, third party = orange,
// rework = teal, failed = red — so the feed and the tables never disagree.
const STATUS_VISUAL = {
  // Universal positives
  "complete":             { icon: CheckCircle2, tone: "green" },
  "completed":            { icon: CheckCircle2, tone: "green" },
  "machining complete":   { icon: CheckCircle2, tone: "green" },
  "done":                 { icon: CheckCircle2, tone: "green" },
  "yes":                  { icon: CheckCircle2, tone: "green" },
  "pass":                 { icon: CheckCircle2, tone: "green" },
  "passed":               { icon: CheckCircle2, tone: "green" },
  "ready":                { icon: CheckCircle2, tone: "green" },
  "delivered":            { icon: CheckCircle2, tone: "green" },
  "shipped":              { icon: Truck,        tone: "green" },
  "received":             { icon: PackageOpen,  tone: "green" },
  // Negatives
  "no":                   { icon: XCircle,      tone: "red" },
  "rejected":             { icon: XCircle,      tone: "red" },
  "fail":                 { icon: XCircle,      tone: "red" },
  "failed":               { icon: XCircle,      tone: "red" },
  "blocked":              { icon: ShieldAlert,  tone: "red" },
  "—":                    { icon: XCircle,      tone: "red" },
  // Rework — teal, matching the rework chip + QC pill
  "rework":               { icon: RotateCcw,    tone: "teal" },
  "rework required":      { icon: RotateCcw,    tone: "teal" },
  // Not-started — neutral, matching the dashed pending pills
  "pending":              { icon: Clock,        tone: "neutral" },
  "queued":               { icon: Inbox,        tone: "neutral" },
  // Waiting states — amber
  "partially received":   { icon: Package,      tone: "amber" },
  "pickup awaited":       { icon: Clock,        tone: "amber" },
  "negotiating rates":    { icon: DollarSign,   tone: "amber" },
  "shipment call raised": { icon: PhoneCall,    tone: "amber" },
  // In-flight states — blue
  "in progress":          { icon: Loader,       tone: "blue" },
  "processing":           { icon: Loader,       tone: "blue" },
  "under inspection":     { icon: Loader,       tone: "blue" },
  "ordered":              { icon: ShoppingCart, tone: "blue" },
  "unblocked":            { icon: ShieldOff,    tone: "blue" },
  // Third party — orange, matching CncMachineIcon's third-party state
  "third party":          { icon: Building2,    tone: "orange" },
};

// Hairline pill vocabulary — same depth language as the stage-table pills.
const TONE_CLASS = {
  green:   "rl-pill border border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400",
  red:     "rl-pill border border-red-500/40 bg-red-500/5 text-red-700 dark:text-red-400",
  amber:   "rl-pill border border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400",
  orange:  "rl-pill border border-orange-500/40 bg-orange-500/5 text-orange-700 dark:text-orange-400",
  blue:    "rl-pill border border-blue-500/40 bg-blue-500/5 text-blue-700 dark:text-blue-400",
  purple:  "rl-pill border border-purple-500/40 bg-purple-500/5 text-purple-700 dark:text-purple-400",
  teal:    "rl-pill border border-teal-500/40 bg-teal-500/5 text-teal-700 dark:text-teal-400",
  neutral: "border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 text-[hsl(var(--foreground))]/80",
};

// Resolve a value (or fall back on action) to a visual definition.
function visualFor(value, action) {
  const key = (value || "").toLowerCase().trim();
  if (STATUS_VISUAL[key]) return STATUS_VISUAL[key];
  // Stage names get their stage icon + orange tone
  if (STAGE_ICONS[value]) {
    return { icon: STAGE_ICONS[value], tone: "orange" };
  }
  // Fall back by action
  if (action === "Stage Advanced")  return { icon: ArrowRight, tone: "green" };
  if (action === "Sent Back")       return { icon: ArrowLeft,  tone: "amber" };
  if (action === "Returned to Customer") return { icon: PackageX, tone: "amber" };
  if (action === "Blocked")         return { icon: ShieldAlert, tone: "red" };
  if (action === "Unblocked")       return { icon: ShieldOff,  tone: "blue" };
  // One of several blocks cleared — the item is still blocked, so this is not
  // the blue all-clear; it's progress on a dependency.
  if (action === "Block Cleared")   return { icon: ShieldOff,  tone: "amber" };
  if (action === "Created")         return { icon: PlusCircle, tone: "orange" };
  if (action === "Note Added")      return { icon: MessageSquare, tone: "neutral" };
  const stock = STOCK_ACTION_VISUALS[action];
  if (stock) return stock;
  return { icon: FileEdit, tone: "neutral" };
}

// Stores actions → icon + tone, mirroring the Stores page activity rail so
// the same movement looks the same everywhere.
const STOCK_ACTION_VISUALS = {
  "Stock Received":     { icon: PackagePlus,      tone: "orange" },
  "Remnant Logged":     { icon: Recycle,          tone: "orange" },
  "Stock Reserved":     { icon: ArrowDownToLine,  tone: "blue" },
  "Stock Released":     { icon: Undo2,            tone: "neutral" },
  "Stock Issued":       { icon: Wrench,           tone: "green" },
  "Stock Adjusted":     { icon: SlidersHorizontal, tone: "amber" },
  "Stock Edited":       { icon: Pencil,           tone: "neutral" },
  "Stock Scrapped":     { icon: Trash2,           tone: "red" },
  "Scrap Reversed":     { icon: Undo2,            tone: "neutral" },
  "Stock Deleted":      { icon: Trash2,           tone: "red" },
  "Stock Bulk Edited":  { icon: Layers,           tone: "neutral" },
  "Stock Bulk Deleted": { icon: Trash2,           tone: "red" },
  // legacy labels written before the past-tense rename
  "Stock Receive":      { icon: PackagePlus,      tone: "orange" },
  "Stock Remnant":      { icon: Recycle,          tone: "orange" },
  "Stock Reserve":      { icon: ArrowDownToLine,  tone: "blue" },
  "Stock Unreserve":    { icon: Undo2,            tone: "neutral" },
  "Stock Issue":        { icon: Wrench,           tone: "green" },
  "Stock Adjust":       { icon: SlidersHorizontal, tone: "amber" },
};

// Parse a changelog entry into [{ label, from, to }] tuples.
function parseChanges(entry) {
  const { action, old_value = "", new_value = "" } = entry;

  if (action === "Stage Advanced")
    return [{ label: "Stage", from: old_value, to: new_value }];

  if (action === "Sent Back") {
    const cleanTo = (new_value || "").split(";")[0].trim();
    return [{ label: "Sent back to", from: old_value, to: cleanTo }];
  }

  if (action === "Returned to Customer")
    return [{ label: "Returned to customer", from: old_value || null, to: new_value || "Dispatch" }];

  if (action === "Created")
    return [{ label: "Created in", from: null, to: new_value || "PO Received" }];

  if (action === "Blocked")
    return [{ label: "Blocked", from: null, to: new_value || "—" }];

  if (action === "Unblocked")
    return [{ label: "Unblocked", from: old_value || null, to: null }];

  if (action === "Block Cleared")
    return [{ label: "Block cleared", from: old_value || null, to: new_value || null }];

  if (action === "Note Added")
    return [{ label: "Note", from: null, to: new_value || "" }];

  if (action === "Status Updated") {
    const newM = new_value.match(/^([^:]+):\s*(.+)$/);
    const oldM = old_value.match(/^([^:]+):\s*(.+)$/);
    const label = (newM?.[1] || oldM?.[1] || "Status").trim();
    return [{
      label,
      from: oldM?.[2]?.trim() || null,
      to: newM?.[2]?.trim() || null,
    }];
  }

  if (action === "Edited") {
    const parts = (new_value || "").split(";").map(s => s.trim()).filter(Boolean);
    const out = parts.map(p => {
      const m = p.match(/^(.+?):\s*(.+?)\s*→\s*(.+)$/);
      if (m) return { label: m[1].trim(), from: m[2].trim(), to: m[3].trim() };
      return { label: "Edited", from: null, to: p };
    });
    return out.length ? out : [{ label: "Edited", from: null, to: "Fields updated" }];
  }

  return [{ label: action, from: old_value || null, to: new_value || null }];
}

// Outcome icon colours — the same tones as the pills, as plain text colour.
const TONE_TEXT = {
  green:   "text-emerald-600 dark:text-emerald-400",
  red:     "text-red-600 dark:text-red-400",
  amber:   "text-amber-600 dark:text-amber-400",
  orange:  "text-orange-600 dark:text-orange-400",
  blue:    "text-blue-600 dark:text-blue-400",
  purple:  "text-purple-600 dark:text-purple-400",
  teal:    "text-teal-600 dark:text-teal-400",
  neutral: "text-[hsl(var(--muted-foreground))]",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-10-21" → "21 Oct" (with the year only when it isn't this year), also
// inside longer values like "Ordered (ETA 2026-09-22)".
function prettyValue(v) {
  if (v === null || v === undefined) return v;
  const thisYear = new Date().getFullYear();
  return String(v).replace(/\b(\d{4})-(\d{2})-(\d{2})(?:T[\d:.]+Z?)?\b/g, (m, y, mo, d) =>
    `${Number(d)} ${MONTHS[Number(mo) - 1] || mo}${Number(y) === thisYear ? "" : ` ${y}`}`);
}

function ValuePill({ value, action, text }) {
  if (value === null || value === undefined) return null;
  const txt = String(value).trim() || "—";
  const v = visualFor(txt, action);
  const Icon = v.icon;
  const shown = text || prettyValue(txt);
  return (
    <span title={shown} className={`inline-flex items-center gap-1 text-[11px] font-medium pl-[7px] pr-2 h-5 rounded-full min-w-0 max-w-[260px] ${TONE_CLASS[v.tone]}`}>
      <Icon className="w-[11px] h-[11px] shrink-0" />
      <span className="truncate">{shown}</span>
    </span>
  );
}

/* FieldChip — a plain "label value" chip for fields that have no status
 * colour (invoice, machine, due date…). Quiet grey so the coloured pills stay
 * the thing the eye finds first. */
function FieldChip({ label, value }) {
  return (
    <span title={`${label}: ${value}`} className="inline-flex items-center gap-[5px] h-5 px-2 rounded-full bg-[hsl(var(--muted))]/60 border border-[hsl(var(--border))]/70 text-[11px] text-[hsl(var(--foreground))]/80 min-w-0 max-w-[260px]">
      <span className="text-[10.5px] text-[hsl(var(--muted-foreground))] shrink-0">{label}</span>
      <span className="font-mono-data text-[10.5px] font-semibold truncate">{prettyValue(value)}</span>
    </span>
  );
}

/* TrackChip — carrier + tracking number as ONE chip. For a known carrier it
 * opens the tracker (same registry + safe opener as the stage rows); an
 * unknown carrier stays a plain chip, never a dead link. */
function TrackChip({ carrier, tracking }) {
  const url = carrierTrackingUrl(carrier, tracking);
  const body = (
    <>
      {carrier && <b className="font-semibold">{carrier}</b>}
      {tracking && <span className="font-mono-data text-[10.5px] font-semibold">{tracking}</span>}
      {url && <ExternalLink className="w-[11px] h-[11px] shrink-0" />}
    </>
  );
  const cls = "inline-flex items-center gap-[5px] h-5 px-2 rounded-full bg-[hsl(var(--muted))]/60 border border-[hsl(var(--border))]/70 text-[11px] text-[hsl(var(--foreground))]/80";
  if (!url) return <span className={cls}>{body}</span>;
  return (
    <button type="button" title="Track this shipment" onClick={(e) => { e.stopPropagation(); openTrackingUrl(url); }}
      className={`${cls} transition-colors hover:border-orange-500/45 hover:text-orange-700 dark:hover:text-orange-400`}>
      {body}
    </button>
  );
}

function NoteChip({ text }) {
  return (
    <span title={text} className="inline-flex items-center gap-[5px] h-5 px-2 rounded-full bg-[hsl(var(--muted))]/60 border border-[hsl(var(--border))]/70 text-[11px] italic text-[hsl(var(--muted-foreground))] min-w-0 max-w-full">
      <MessageSquare className="w-[11px] h-[11px] shrink-0" />
      <span className="truncate">{text}</span>
    </span>
  );
}

/* Clock for a burst. The day header says which day, so only today's last
 * three hours read relative ("40m", "2h"); everything else is a time. */
function clock(ts) {
  const d = new Date(ts);
  const s = (Date.now() - d) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 10800 && d.toDateString() === new Date().toDateString()) return `${Math.floor(s / 3600)}h`;
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function dayHeading(ts) {
  const d = new Date(ts);
  const today = new Date();
  const yest = new Date(today); yest.setDate(today.getDate() - 1);
  const date = `${d.getDate()} ${MONTHS[d.getMonth()]}${d.getFullYear() === today.getFullYear() ? "" : ` ${d.getFullYear()}`}`;
  if (d.toDateString() === today.toDateString()) return { title: "Today", date };
  if (d.toDateString() === yest.toDateString()) return { title: "Yesterday", date };
  return { title: d.toLocaleDateString("en-GB", { weekday: "long" }), date };
}

function shortName(user) {
  if (!user) return "?";
  return user.replace(/\s*\(.*\)/, "").trim().split(" ")[0];
}

// Stable soft hue per person, so the same name always wears the same avatar.
function nameHue(name) {
  let h = 0;
  for (const c of name || "?") h = (h * 31 + c.charCodeAt(0)) % 360;
  return h;
}

/* ── Grouping: burst → items → changes ─────────────────────────────────────
 * A burst is one person's run of updates with no gap over 5 minutes. Inside
 * it every LINE appears once, carrying all of its changes, so five field edits
 * on one shipment read as one row instead of five (Tushar, 3-Nov-2026, mock
 * plans/activity-dispatch-mock.html option B). */
const BURST_GAP_MS = 5 * 60 * 1000;

function itemKey(e) {
  if (e.is_bulk) return `bulk-${e.id}`;
  return e.item_id || [e.po_number, e.line_number, e.part_number, e.item_description].join("|") || e.id;
}

// Most telling change first: it becomes the row's icon and drives the sentence.
const KIND_RANK = ["shipped", "blocked", "sentBack", "returned", "created", "advanced", "unblocked", "cleared", "status", "stock", "edited", "note"];

function changeKind(c) {
  const a = c.action;
  if (/dispatch status/i.test(c.label || "") && /^(shipped|delivered)$/i.test((c.to || "").trim())) return "shipped";
  if (a === "Blocked") return "blocked";
  if (a === "Sent Back") return "sentBack";
  if (a === "Returned to Customer") return "returned";
  if (a === "Created") return "created";
  if (a === "Stage Advanced") return "advanced";
  if (a === "Unblocked") return "unblocked";
  if (a === "Block Cleared") return "cleared";
  if (a === "Status Updated") return "status";
  if (STOCK_ACTION_VISUALS[a]) return "stock";
  if (a === "Note Added") return "note";
  return "edited";
}

function buildItems(entries) {
  const map = new Map();
  entries.forEach((e) => {
    const k = itemKey(e);
    if (!map.has(k)) map.set(k, { key: k, entry: e, entries: [] });
    map.get(k).entries.push(e);
  });
  return [...map.values()].map((it) => {
    // Oldest → newest, so a field changed twice keeps its latest value.
    const byLabel = new Map();
    const notes = [];
    [...it.entries].reverse().forEach((e) => {
      parseChanges(e).forEach((c) => {
        const ch = { ...c, action: e.action };
        if (e.action === "Note Added") notes.push(ch);
        else byLabel.set((c.label || e.action).toLowerCase(), ch);
      });
    });
    const changes = [...byLabel.values(), ...notes];
    let outcome = null;
    let rank = Infinity;
    changes.forEach((c) => {
      const kind = changeKind(c);
      const r = KIND_RANK.indexOf(kind);
      if (r < rank) { rank = r; outcome = { ...c, kind }; }
    });
    return { ...it, changes, outcome };
  });
}

function buildBursts(entries) {
  const out = [];
  let cur = null;
  entries.forEach((e) => {
    const ts = new Date(e.timestamp).getTime();
    if (cur && cur.user === e.user && Number.isFinite(ts) && cur.lastTs - ts <= BURST_GAP_MS) {
      cur.entries.push(e);
      cur.lastTs = ts;
    } else {
      cur = { user: e.user, entries: [e], lastTs: ts };
      out.push(cur);
    }
  });
  return out.map((b) => {
    const stages = [...new Set(b.entries.map((e) => e.stage).filter(Boolean))];
    return { ...b, id: b.entries[0].id, latest: b.entries[0], stages, items: buildItems(b.entries) };
  });
}

function outcomeVisual(o) {
  if (!o) return { icon: FileEdit, tone: "neutral" };
  if (o.kind === "created")  return { icon: PlusCircle, tone: "blue" };
  if (o.kind === "advanced") return { icon: ArrowRight, tone: "green" };
  if (o.kind === "sentBack") return { icon: ArrowLeft, tone: "amber" };
  if (o.kind === "note")     return { icon: MessageSquare, tone: "neutral" };
  if (o.kind === "edited")   return { icon: FileEdit, tone: "neutral" };
  return visualFor(o.to, o.action);
}

const STOCK_VERBS = {
  "Stock Received": "received", "Stock Receive": "received",
  "Remnant Logged": "logged remnants for", "Stock Remnant": "logged remnants for",
  "Stock Reserved": "reserved", "Stock Reserve": "reserved",
  "Stock Released": "released", "Stock Unreserve": "released",
  "Stock Issued": "issued", "Stock Issue": "issued",
  "Stock Adjusted": "adjusted", "Stock Adjust": "adjusted",
  "Stock Edited": "edited", "Stock Bulk Edited": "edited",
  "Stock Scrapped": "scrapped", "Scrap Reversed": "reversed scrap on",
  "Stock Deleted": "deleted", "Stock Bulk Deleted": "deleted",
};

/* One plain sentence per burst: "shipped 3 lines", "moved 2 lines to Quality
 * Control". Mixed bursts fall back to "updated N lines"; the row icons carry
 * the detail. */
function BurstSentence({ items, stores }) {
  const n = items.reduce((s, it) => s + (it.entry.is_bulk ? (it.entry.bulk_count || 1) : 1), 0);
  const Count = ({ word = stores ? "stock item" : "line" }) => (
    <b className="font-semibold text-[hsl(var(--foreground))]">{n} {word}{n === 1 ? "" : "s"}</b>
  );
  const kinds = new Set(items.map((it) => it.outcome?.kind));
  if (kinds.size !== 1) return <>updated <Count /></>;
  const o = items[0].outcome || {};
  const same = (f) => items.every((it) => (it.outcome?.[f] || "") === (o[f] || ""));
  switch (o.kind) {
    case "shipped":   return <>shipped <Count /></>;
    case "created":   return <>added <Count word="PO line" /></>;
    case "advanced":  return same("to") ? <>moved <Count /> to {o.to}</> : <>moved <Count /> on</>;
    case "sentBack":  return same("to") ? <>sent <Count /> back to {o.to}</> : <>sent <Count /> back</>;
    case "returned":  return <>returned <Count /> to the customer</>;
    case "blocked":   return <>blocked <Count /></>;
    case "unblocked": return <>unblocked <Count /></>;
    case "cleared":   return <>cleared a block on <Count /></>;
    case "status":
      if (!same("label")) return <>updated <Count /></>;
      return same("to") ? <>set {o.label} to {prettyValue(o.to)} on <Count /></> : <>updated {o.label} on <Count /></>;
    case "stock":     return same("action") ? <>{STOCK_VERBS[o.action] || "updated"} <Count word="stock item" /></> : <>updated <Count word="stock item" /></>;
    case "note":      return <>added a note on <Count /></>;
    default:          return same("label") && o.label && o.label !== "Edited" ? <>edited {o.label} on <Count /></> : <>edited <Count /></>;
  }
}

// Every change on a line, as chips: status values keep their tone; carrier +
// tracking merge into one track chip; the ship date joins the Shipped pill.
function DetailChips({ item }) {
  const byLabel = {};
  item.changes.forEach((c) => { if (c.action !== "Note Added") byLabel[(c.label || "").toLowerCase()] = c; });
  const carrier = byLabel["carrier"] || byLabel["carrier name"];
  const tracking = byLabel["tracking"] || byLabel["tracking id"] || byLabel["tracking number"];
  const shippedOn = byLabel["shipped on"] || byLabel["shipped at"];
  const used = new Set([carrier, tracking].filter(Boolean));
  const chips = [];
  item.changes.forEach((c, i) => {
    if (used.has(c)) return;
    if (c.action === "Note Added") { if (c.to) chips.push(<NoteChip key={i} text={c.to} />); return; }
    const kind = changeKind(c);
    if (kind === "shipped") {
      const when = shippedOn?.to && shippedOn.to !== "—" ? ` ${prettyValue(shippedOn.to)}` : "";
      if (shippedOn) used.add(shippedOn);
      chips.push(<ValuePill key={i} value={c.to} action={c.action} text={`${c.to}${when}`} />);
      return;
    }
    if (c === shippedOn && item.changes.some((x) => changeKind(x) === "shipped")) return;
    if (kind === "edited" || (kind === "status" && visualFor(c.to, c.action).tone === "neutral")) {
      if (c.to !== null && c.to !== undefined) chips.push(<FieldChip key={i} label={c.label} value={c.to} />);
      return;
    }
    if (kind === "unblocked") { chips.push(<ValuePill key={i} value="Unblocked" action={c.action} />); return; }
    if (kind === "blocked") { chips.push(<ValuePill key={i} value={c.to} action={c.action} text={`Blocked · ${prettyValue(c.to)}`} />); return; }
    if (kind === "created") { chips.push(<ValuePill key={i} value="New line" action="Created" text={`New in ${c.to}`} />); return; }
    if (kind === "advanced") { chips.push(<ValuePill key={i} value={c.to} action={c.action} text={`Moved to ${c.to}`} />); return; }
    if (kind === "sentBack") { chips.push(<ValuePill key={i} value={c.to} action={c.action} text={`Sent back to ${c.to}`} />); return; }
    if (c.to !== null && c.to !== undefined) {
      chips.push(kind === "status" && c.label && !/status/i.test(c.label)
        ? <ValuePill key={i} value={c.to} action={c.action} text={`${c.label} · ${prettyValue(c.to)}`} />
        : <ValuePill key={i} value={c.to} action={c.action} />);
    }
  });
  if (carrier || tracking) chips.push(<TrackChip key="trk" carrier={carrier?.to !== "—" ? carrier?.to : ""} tracking={tracking?.to !== "—" ? tracking?.to : ""} />);
  return chips;
}

const ROW_LIMIT = 4;

export default function LiveUpdatesCard({ token, className = "" }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isNew, setIsNew] = useState(false);
  const [openDetails, setOpenDetails] = useState({});   // burst id → details shown
  const [showAll, setShowAll] = useState({});           // burst id → all rows shown
  const prevTopId = useRef(null);
  const navigate = useNavigate();

  // Client identity for the feed — resolve each client's logo with the same
  // client → subgroup → group fallback used on the stage rows + History Log.
  const { clients } = useContext(ClientsContext) || {};
  const { clientGroups } = useContext(ClientGroupsContext) || {};
  const clientLogoMap = useMemo(() => {
    const map = {};
    const groups = clientGroups || [];
    const byId = {};
    groups.forEach(g => { if (g.id) byId[g.id] = g; });
    (clients || []).forEach(c => {
      if (!c.name) return;
      if (c.logo_svg && c.logo_svg.trim()) { map[c.name] = c.logo_svg; return; }
      // The group/subgroup that directly lists this client…
      const container = groups.find(g => (g.client_ids || []).includes(c.id));
      if (!container) return;
      if (container.logo_svg && container.logo_svg.trim()) { map[c.name] = container.logo_svg; return; }
      // …and if that's a logoless subgroup, fall back to its parent group.
      const parent = container.parent_id ? byId[container.parent_id] : null;
      if (parent?.logo_svg && parent.logo_svg.trim()) { map[c.name] = parent.logo_svg; }
    });
    return map;
  }, [clients, clientGroups]);

  const goToItem = useCallback((entry) => {
    const path = STAGE_PATHS[entry.stage];
    const q = entry.part_number || entry.po_number;
    if (path && q) navigate(`${path}?q=${encodeURIComponent(q)}`);
  }, [navigate]);

  const fetchFeed = () => {
    if (!token) return;
    axios.get(`${API}/api/changelog?limit=120`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : [];
        // Auth (logins) and Admin (user management) entries stay in the
        // History page but are too sensitive/noisy for the live feed.
        const visible = list.filter(e => e.stage !== "Auth" && e.stage !== "Admin");
        if (visible.length && visible[0].id !== prevTopId.current) {
          if (prevTopId.current !== null) setIsNew(true);
          prevTopId.current = visible[0]?.id ?? null;
        }
        setEntries(visible);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchFeed();
    const id = setInterval(fetchFeed, 12000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!isNew) return;
    const t = setTimeout(() => setIsNew(false), 2000);
    return () => clearTimeout(t);
  }, [isNew]);

  const bursts = useMemo(() => buildBursts(entries), [entries]);

  const clientMark = (client) => {
    if (!client) return null;
    const logo = clientLogoMap[client];
    if (logo) {
      // Recessed grey: the brand mark greyed + set back, quiet client identity.
      return (
        <span aria-hidden="true" className="inline-flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full"
          style={{ width: 13, height: 13, filter: "grayscale(1)", opacity: 0.55 }}
          dangerouslySetInnerHTML={{ __html: logo }} />
      );
    }
    const initials = client.split(" ").slice(0, 2).map(w => w[0]).join("");
    return (
      <span aria-hidden="true" className="inline-flex items-center justify-center shrink-0 rounded-full text-[6px] font-bold leading-none text-[hsl(var(--muted-foreground))]"
        style={{ width: 13, height: 13, backgroundColor: "hsl(var(--muted-foreground) / 0.15)" }}>
        {initials}
      </span>
    );
  };

  const renderRow = (burst, it) => {
    const e = it.entry;
    const { icon: Icon, tone } = outcomeVisual(it.outcome);
    const lineNum = e.line_number ? parseFloat(e.line_number).toFixed(0) : null;
    const desc = e.item_description || e.part_number || e.po_number || it.outcome?.label || "—";
    const poLine = e.po_number ? `PO ${e.po_number}${lineNum ? ` · L${lineNum}` : ""}` : "";
    const hint = (it.outcome?.kind === "note" || it.outcome?.kind === "blocked") ? it.outcome.to : null;
    const canOpen = !!(STAGE_PATHS[e.stage] && (e.part_number || e.po_number));
    return (
      <div key={it.key}>
        <button
          type="button"
          onClick={() => canOpen && goToItem(e)}
          title={[e.client, canOpen ? `Open in ${e.stage}` : null].filter(Boolean).join(" · ")}
          data-testid={`activity-item-link-${e.id}`}
          className={`group/row grid w-full grid-cols-[14px_minmax(0,1fr)_auto_auto] items-center gap-[7px] px-1.5 -mx-1.5 py-[3px] rounded-md text-left transition-colors ${canOpen ? "hover:bg-orange-500/[0.06] cursor-pointer" : "cursor-default"}`}
        >
          <Icon className={`w-3 h-3 ${TONE_TEXT[tone] || TONE_TEXT.neutral}`} aria-hidden="true" />
          <span title={desc} className={`truncate text-[11.5px] text-[hsl(var(--foreground))]/85 ${canOpen ? "group-hover/row:text-orange-700 dark:group-hover/row:text-orange-400" : ""}`}>{desc}</span>
          {/* Faint client logo, then the part number. */}
          <span className="inline-flex items-center gap-1.5 justify-self-end">
            {clientMark(e.client)}
            {e.part_number && (
              <span className="font-mono-data text-[10px] font-semibold px-1.5 py-px rounded-[5px] rl-carved bg-[hsl(var(--muted))]/50 text-[hsl(var(--foreground))]/80 whitespace-nowrap">{e.part_number}</span>
            )}
          </span>
          {/* PO + line are the pair; the part number stands on its own. */}
          <span className="font-mono-data text-[10px] text-[hsl(var(--muted-foreground))]/70 whitespace-nowrap text-right">{poLine}</span>
        </button>
        {hint && !openDetails[burst.id] && (
          <div className="ml-[21px] -mt-px mb-1 text-[11px] italic text-[hsl(var(--muted-foreground))] line-clamp-2">{prettyValue(hint)}</div>
        )}
        {openDetails[burst.id] && (
          <div className="ml-[21px] mt-0.5 mb-1.5 flex flex-wrap items-center gap-1">
            <DetailChips item={it} />
            {e.client && (
              <span className="inline-flex items-center gap-1.5 ml-1 text-[10.5px] text-[hsl(var(--muted-foreground))]/80 whitespace-nowrap">
{e.client}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderFeed = () => {
    let lastDay = null;
    const out = [];
    bursts.forEach((b, bi) => {
      const day = new Date(b.latest.timestamp).toDateString();
      const prevDay = bi > 0 ? new Date(bursts[bi - 1].latest.timestamp).toDateString() : null;
      const nextDay = bi < bursts.length - 1 ? new Date(bursts[bi + 1].latest.timestamp).toDateString() : null;
      if (day !== lastDay) {
        const h = dayHeading(b.latest.timestamp);
        out.push(
          <div key={`day-${day}`} className="sticky top-0 z-[2] flex items-center gap-2 px-2 pt-2 pb-1.5 bg-[hsl(var(--card))]">
            <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))] whitespace-nowrap">
              {h.title}<span className="ml-1.5 normal-case tracking-[0.04em] text-[10.5px] text-[hsl(var(--muted-foreground))]/60">{h.date}</span>
            </span>
            <span className="flex-1 h-px bg-[hsl(var(--border))]/60" />
          </div>
        );
        lastDay = day;
      }
      const first = prevDay !== day;
      const last = nextDay !== day;
      const stage = b.stages[0];
      const ring = STAGE_COLORS[stage]?.color || "hsl(var(--border))";
      const name = shortName(b.user);
      const rows = showAll[b.id] ? b.items : b.items.slice(0, ROW_LIMIT);
      const hiddenRows = b.items.length - rows.length;
      const detailsOpen = !!openDetails[b.id];
      out.push(
        <div key={`burst-${b.id}`} className="relative pl-10 pr-2 pt-[9px] pb-[11px]" data-testid={`activity-burst-${bi}`}>
          {/* The thread: joins this person's node to the ones above and below
              on the same day. */}
          {!(first && last) && (
            <span aria-hidden="true" className="absolute left-[19.5px] w-px bg-[hsl(var(--border))]"
              style={first ? { top: 20, bottom: 0 } : last ? { top: 0, height: 20 } : { top: 0, bottom: 0 }} />
          )}
          <span className="absolute left-[7px] top-2 z-[1] rounded-full p-[2px] bg-[hsl(var(--card))]">
            <span
              className="rl-av inline-flex items-center justify-center w-[22px] h-[22px] rounded-full font-heading text-[9.5px] font-bold tracking-[0.02em]"
              style={{ "--av-h": nameHue(name), boxShadow: `0 0 0 1.5px ${ring}` }}
              title={b.user}
              aria-hidden="true"
            >
              {name[0]?.toUpperCase()}
            </span>
          </span>
          <div className="flex items-baseline gap-2 min-h-[22px] pt-[3px]">
            <span className="flex-1 min-w-0 text-[12.5px] leading-snug text-[hsl(var(--foreground))]/75">
              <b className="font-semibold text-[hsl(var(--foreground))]">{name}</b>{" "}
              <BurstSentence items={b.items} stores={stage === "Stores"} />
              {b.stages.map((s) => (
                <span key={s} className="inline-flex items-center gap-[5px] ml-2 text-[11px] text-[hsl(var(--muted-foreground))] whitespace-nowrap align-baseline">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0 self-center" style={{ background: STAGE_COLORS[s]?.color || "#94a3b8" }} />
                  {s}
                </span>
              ))}
            </span>
            <span className="font-mono-data text-[11px] text-[hsl(var(--muted-foreground))]/70 whitespace-nowrap shrink-0">{clock(b.latest.timestamp)}</span>
          </div>
          <div className="mt-1 flex flex-col">
            {rows.map((it) => renderRow(b, it))}
          </div>
          <div className="flex items-center gap-3 mt-[3px]">
            <button type="button" onClick={() => setOpenDetails((p) => ({ ...p, [b.id]: !p[b.id] }))}
              className="inline-flex items-center gap-[3px] text-[10.5px] font-medium text-[hsl(var(--muted-foreground))]/70 hover:text-[hsl(var(--foreground))] transition-colors"
              data-testid={`activity-details-${bi}`}>
              {detailsOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {detailsOpen ? "Hide details" : "Details"}
            </button>
            {hiddenRows > 0 && (
              <button type="button" onClick={() => setShowAll((p) => ({ ...p, [b.id]: true }))}
                className="text-[10.5px] font-medium text-[hsl(var(--muted-foreground))]/70 hover:text-[hsl(var(--foreground))] transition-colors">
                + {hiddenRows} more line{hiddenRows === 1 ? "" : "s"}
              </button>
            )}
          </div>
        </div>
      );
    });
    return out;
  };

  return (
    <div
      className={`rl-widget-glass rounded-xl flex flex-col min-h-0 overflow-hidden ${className}`}
      data-testid="live-updates-card"
    >
      {/* ── Header: the hero's label type + the streaming wake. The row is 28px
          (box-content h-7), the same as the Monthly Dispatches header with its
          round buttons, so the two titles sit on one line. ── */}
      <div className="box-content h-7 flex items-center justify-between gap-3 pl-6 pr-[22px] pt-[18px] pb-2 shrink-0">
        <h3 className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">
          Live Activity
          {/* Wake, not a ping: the feed reports work moving down the pipeline,
              so the mark drifts downstream. Goes emerald for 2s on new entries. */}
          <LiveWake color={isNew ? "#10b981" : "#f97316"} />
        </h3>
        <span className="text-[10.5px] text-[hsl(var(--muted-foreground))]/60 whitespace-nowrap">Everyone · all divisions</span>
      </div>

      {/* ── Feed: a timeline, one burst per person ── */}
      <div className="flex-1 min-h-0 overflow-y-auto pl-4 pr-3.5 pb-1.5 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[hsl(var(--border))] [&::-webkit-scrollbar-track]:bg-transparent">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-24 text-[11px] text-[hsl(var(--muted-foreground))]">
            <Loader className="w-4 h-4 animate-spin text-[hsl(var(--muted-foreground))]/40 mb-1" />
            Loading…
          </div>
        ) : bursts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-center">
            <div className="text-[12.5px] font-medium text-[hsl(var(--foreground))]">No activity yet</div>
            <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">Updates from every stage show up here as they happen.</div>
          </div>
        ) : renderFeed()}
      </div>

      {/* ── Footer ── */}
      <button
        type="button"
        onClick={() => navigate("/changelog")}
        className="rl-chip-host shrink-0 flex items-center justify-between gap-3 pl-6 pr-6 pt-[9px] pb-[11px] border-t border-[hsl(var(--border))]/60 text-left focus-visible:outline-none"
        data-testid="view-full-history-btn"
      >
        <span className="text-[10.5px] text-[hsl(var(--muted-foreground))]/70">Last {entries.length} updates</span>
        <span className="rl-open-chip">Full history <ArrowRight className="rl-open-arrow w-3 h-3" strokeWidth={2.4} /></span>
      </button>
    </div>
  );
}
